import { formatCents, formatCount, parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The customer browser, and the four things that go out from it to real people.
 *
 * `/marketing-customers` is the one screen in this application whose buttons reach outside it:
 * e-mail, SMS, web push. 2 464 customers in the restored data, 2 059 with an address, 114 live push
 * subscriptions. Everything here exists because a mistake on this page is not a wrong number on a
 * screen — it is 2 000 people receiving something.
 *
 * **FB-093.** `CustomerBulkService.sendEmail` is introduced by the comment *"The same, by e-mail, and
 * consent is checked the same way."* It is not:
 *
 * ```java
 * // sendSms
 * "... WHERE id IN (:ids) AND phone IS NOT NULL AND phone <> '' AND marketing_sms_opt_in = true"
 * // sendEmail
 * "SELECT id, name, email, points_balance, order_count FROM customers"
 *     + " WHERE id IN (:ids) AND email IS NOT NULL AND email <> ''"
 * ```
 *
 * No `marketing_email_opt_in`. 79 of the 2 059 addressable customers have explicitly opted out and
 * would be mailed anyway, and the response carries no `skipped` count, so nothing on the screen shows
 * that consent was never consulted. Legacy's interface has the same hole in the same place: the
 * per-customer e-mail button is disabled on `!marketingEmailOptIn`, and the *bulk* composer has no
 * eligibility count at all — while the SMS one is titled *"Hromadná SMS — {eligible} z {count}"*.
 *
 * So this page filters the recipients itself, states how many it left out and why, and the backend
 * item is filed. A frontend cannot make the server ask; it can decline to hand it people who said no.
 *
 * **FB-094.** The composer advertises variables the server does not substitute, and substitutes
 * nothing in a subject line. `EMAIL_VARS` promises `{{name}}`, `{{email}}` and `{{programName}}`;
 * `render()` handles `{{name}}`, `{{points}}`, `{{orders}}` and their single-brace spellings — and is
 * applied to the body only, never the subject. Follow the legend and 2 059 people receive an e-mail
 * containing the literal text `{{email}}`.
 *
 * **FB-095.** An SMS is transliterated before it is sent — `SmsGateway.stripDiacritics`, whose own
 * javadoc says *"Slovak without accents — the difference between one SMS segment and three"*. The
 * composer counts segments on what was typed, so a message full of diacritics is costed at 160
 * characters a segment when the customer will receive the stripped version. Neither the strip nor the
 * bill is shown: legacy prints the segment count for the message and never multiplies it by the
 * recipients.
 *
 * **FB-096.** A newsletter-only row can be ticked for a bulk action it can never receive. Legacy
 * guards the row *click* — `if (!c._newsletterOnly && c.id > 0)` — and not the checkbox, so a
 * negative synthetic id joins `recipientIds` and matches nobody.
 */

// ─── shapes ──────────────────────────────────────────────────────────────────

export interface Customer {
  id?: number
  email?: string | null
  name?: string | null
  phone?: string | null
  isLoyalty?: boolean
  loyaltyLevel?: string | null
  customLevelKey?: string | null
  pointsBalance?: number | null
  orderCount?: number | null
  totalSpent?: MoneyInput
  lastOrderAt?: string | null
  createdAt?: string | null
  marketingEmailOptIn?: boolean
  marketingSmsOptIn?: boolean
  deletionRequestedAt?: string | null
  referredCount?: number | null
  wasReferred?: boolean
  personalDiscountType?: 'percent' | 'fixed' | null
  personalDiscountValue?: MoneyInput
  personalDiscountStackable?: boolean
  /**
   * How many browsers this customer has registered for push — §10A.92.
   *
   * On the **list** since that entry; it was on the detail alone, which is why this page could not
   * tell who has push and said so in `ineligibleReason`.
   */
  pushSubscriptionCount?: number
  /** Set by the server on rows lifted out of `newsletter_subscribers`; the id is negative. */
  _newsletterOnly?: boolean
}

export interface CustomerDetail extends Customer {
  orders?: {
    id?: number
    orderNumber?: string
    status?: string
    type?: string
    paymentMethod?: string
    total?: MoneyInput
    createdAt?: string | null
  }[]
  newsletterSubscribed?: boolean
  referralsSent?: {
    id?: number
    status?: string
    rewardPoints?: number | null
    createdAt?: string | null
    referredId?: number | null
    referredName?: string | null
    referredEmail?: string | null
  }[]
  referredBy?: {
    id?: number
    status?: string
    createdAt?: string | null
    referrerId?: number | null
    referrerName?: string | null
    referrerEmail?: string | null
  } | null
}

export interface LoyaltyLevel {
  id?: number
  key?: string
  name?: string
  emoji?: string | null
  color?: string | null
  active?: boolean
}

/** A row that only exists in `newsletter_subscribers` — no customer record behind it. */
export function isNewsletterOnly(customer: Customer): boolean {
  return customer._newsletterOnly === true || (customer.id ?? 0) < 0
}

export function displayName(customer: Customer): string {
  const name = (customer.name ?? '').trim()
  if (name !== '') return name
  const email = (customer.email ?? '').trim()
  if (email !== '') return email
  const phone = (customer.phone ?? '').trim()
  if (phone !== '') return phone
  return `#${customer.id ?? 0}`
}

// ─── consent, per channel ────────────────────────────────────────────────────

export type Channel = 'email' | 'sms' | 'push'

export type Ineligible =
  | 'newsletter-only'
  | 'no-email'
  | 'no-phone'
  | 'opted-out'
  | 'no-device'
  | 'deactivated'

/**
 * Why this customer would not receive a message on this channel — the *server's* rules.
 *
 * **The server enforces all of these now** (`MIGRATION-STATUS` FB-094, 2026-08-02). `sendEmail` used
 * to filter on a non-empty address and nothing else, so it mailed anybody it was handed and its
 * empty-list branch mailed every loyalty member — this function was the only guard, which meant any
 * other caller of that endpoint had none. Neither channel checked `deletion_requested_at` at all.
 *
 * So this is belt-and-braces rather than the only defence, which is what it was built to be. It still
 * earns its place: it says **why** somebody is excluded, on the screen, before a send is attempted —
 * the server can only refuse, and a count that silently shrinks tells nobody anything.
 */
export function ineligibleReason(customer: Customer, channel: Channel): Ineligible | null {
  if (isNewsletterOnly(customer)) return 'newsletter-only'
  if ((customer.deletionRequestedAt ?? null) !== null) return 'deactivated'

  if (channel === 'email') {
    if ((customer.email ?? '').trim() === '') return 'no-email'
    if (customer.marketingEmailOptIn === false) return 'opted-out'
    return null
  }
  if (channel === 'sms') {
    if ((customer.phone ?? '').trim() === '') return 'no-phone'
    if (customer.marketingSmsOptIn === false) return 'opted-out'
    return null
  }
  // Push consent is the subscription itself — a customer grants it in their own browser, and there
  // is no opt-in column to consult. **The list carries a device count since §10A.92**, so the honest
  // answer is no longer *the server will decide*: a customer with no registered browser cannot be
  // reached, and `no-device` has been declared in `Ineligible` since this file was written, waiting
  // for exactly that. Measured: 114 subscriptions across 104 of 2 463 customers.
  //
  // Absent is treated as **no device**, deliberately. A page that has not been told cannot promise a
  // delivery, and the old answer — everybody eligible — is the one that would have selected two and
  // a half thousand people for a channel 104 of them have.
  return (customer.pushSubscriptionCount ?? 0) > 0 ? null : 'no-device'
}

export function isEligible(customer: Customer, channel: Channel): boolean {
  return ineligibleReason(customer, channel) === null
}

/**
 * The drawer's push row - `FRONTEND.md` §9.735, owner O104.
 *
 * E-mail and SMS consent are flags on the customer's record; push has none, because a subscription *is* the
 * customer's browser handing over an endpoint and keys. So the row is **on** when the customer has a device, the
 * manager may switch it **off** (which removes every one, §10A.381), and **on** is never the manager's to give:
 * the note says who can.
 */
export interface PushConsent {
  /** Registered browsers, as the server counted them. */
  devices: number
  checked: boolean
  /** A real customer with a device - a newsletter-only row has a negative id and nothing to remove. */
  mayTurnOff: boolean
  note: string
}

export function pushConsent(customerId: number, pushSubscriptionCount: number | undefined): PushConsent {
  const devices =
    typeof pushSubscriptionCount === 'number' && Number.isFinite(pushSubscriptionCount) && pushSubscriptionCount > 0
      ? Math.floor(pushSubscriptionCount)
      : 0
  const onlyTheCustomer = tt('Zapnúť ich môže len zákazník sám vo svojom prehliadači - účet → Notifikácie.')
  return {
    devices,
    checked: devices > 0,
    mayTurnOff: devices > 0 && customerId > 0,
    note:
      devices > 0
        ? `${tt('Vypnutím sa odhlásia všetky zariadenia zákazníka.')} ${onlyTheCustomer}`
        : onlyTheCustomer,
  }
}

export interface Audience {
  channel: Channel
  selected: number
  eligible: Customer[]
  excluded: { customer: Customer; reason: Ineligible }[]
  /** How many were dropped for each reason, for the sentence on the screen. */
  reasons: Partial<Record<Ineligible, number>>
  /** True when push, where the page cannot know and says so. */
  serverDecides: boolean
}

export function audienceFor(
  customers: Customer[],
  selected: ReadonlySet<number>,
  channel: Channel,
): Audience {
  const eligible: Customer[] = []
  const excluded: { customer: Customer; reason: Ineligible }[] = []
  const reasons: Partial<Record<Ineligible, number>> = {}

  for (const customer of customers) {
    if (customer.id === undefined || !selected.has(customer.id)) continue
    const reason = ineligibleReason(customer, channel)
    if (reason === null) {
      eligible.push(customer)
      continue
    }
    excluded.push({ customer, reason })
    reasons[reason] = (reasons[reason] ?? 0) + 1
  }

  return {
    channel,
    selected: selected.size,
    eligible,
    excluded,
    reasons,
    // §10A.92 — the page can answer for push now, so it stops saying the server will.
    serverDecides: false,
  }
}

export function recipientIds(audience: Audience): number[] {
  return audience.eligible.map((customer) => customer.id as number)
}

/**
 * One customer as an audience — §9.336, the owner's 5b.
 *
 * The drawer's three send buttons and the bulk bar's are the same buttons, and this is why they can
 * be. `audienceFor` was written against a **set**, not against a page, so a selection of one carries
 * the same eligibility verdict, the same `reasons` map and the same `serverDecides` flag as a
 * selection of two thousand — and `ComposeDialog` opens on either without knowing which it has.
 *
 * **The alternative is the bug FB-093 found.** Legacy has the consent rule in two places and they
 * disagree: its per-customer button disables on an opt-out while its bulk composer never counted
 * eligibility at all. A second copy of these rules for the single case would be that, reversed.
 *
 * A customer with no id cannot be addressed by any of the three endpoints, so it comes back as an
 * empty audience rather than one the page would offer to send to.
 */
export function audienceOfOne(customer: Customer, channel: Channel): Audience {
  if (customer.id === undefined) return audienceFor([], new Set(), channel)
  return audienceFor([customer], new Set([customer.id]), channel)
}

/**
 * Who a send is going to, when it is going to exactly one person.
 *
 * **A count is the wrong question for one recipient.** *Odoslať 1 príjemcovi?* is the sentence the
 * bulk confirmation would produce, and it withholds the one fact that matters: `BLOCKED.md` opens
 * with this page because these three buttons are the only ones in the manager that reach a real
 * human being from a single click. So the confirmation names them, and names the address or number
 * the message is about to leave for.
 *
 * Empty for any other size, so the caller keeps the count for a selection - where the count *is* the
 * fact, and thirty names would not fit in a dialog.
 */
export function singleRecipientLabel(audience: Audience): string {
  if (audience.eligible.length !== 1) return ''
  const only = audience.eligible[0] as Customer
  const address =
    audience.channel === 'email' ? (only.email ?? '').trim() : (only.phone ?? '').trim()
  const name = displayName(only)
  // Push has no address to name: the message goes to however many browsers they registered, and the
  // device count is the honest second half of that sentence.
  if (audience.channel === 'push') {
    return `${name} - ${only.pushSubscriptionCount ?? 0} ${tt('zariadení')}`
  }
  return address === '' || address === name ? name : `${name} - ${address}`
}

export function exclusionText(reason: Ineligible, count: number): string {
  const suffix = ` (${count})`
  if (reason === 'opted-out') return tt('odhlásení z marketingu') + suffix
  if (reason === 'no-email') return tt('bez e-mailu') + suffix
  if (reason === 'no-phone') return tt('bez telefónu') + suffix
  if (reason === 'no-device') return tt('bez prihláseného zariadenia') + suffix
  if (reason === 'deactivated') return tt('deaktivovaný účet') + suffix
  return tt('iba newsletter, bez zákazníckeho záznamu') + suffix
}

// ─── the message itself ──────────────────────────────────────────────────────

/**
 * What each channel's sender actually replaces - §9.484, read off the three send paths.
 *
 * **This was one list of three for every channel and that was wrong two ways.** Its comment claimed to
 * mirror `CustomerBulkService.render`, which replaces **five**; FB-094 was fixed by *implementing*
 * `{{email}}` and `{{programName}}` and this list was never widened, so the composer called both unknown
 * and blocked the send - about the two variables that entry exists to have fixed.
 *
 * | channel | substituted | where |
 * |---|---|---|
 * | e-mail | name, points, orders, email, programName | `render(subject, …)` **and** `render(body, …)` |
 * | SMS | name, points, orders, email | `render(message, customer, **null**)` - `programName` is null on
 *   that path, and `render` leaves an unknown variable alone rather than writing the word "null" |
 * | push | name, points, orders | `CustomerPushService.fill`, a separate method with three |
 *
 * **Per channel rather than one widened list**, because `programName` is genuinely unavailable on SMS and
 * push: promising it there would be this defect in the other direction.
 *
 * Both spellings are handled by every one of the three senders, so the token's braces are not part of the
 * question - `VARIABLE_PATTERN` matches either.
 *
 * `BulkMessageVariablesAgreeTest` in the backend holds this table against the Java, in both directions,
 * because a frontend test cannot see `render` and that is precisely how this drifted.
 */
export const SUBSTITUTED_BY_CHANNEL = {
  email: ['name', 'points', 'orders', 'email', 'programName'],
  sms: ['name', 'points', 'orders', 'email'],
  push: ['name', 'points', 'orders'],
} as const satisfies Record<string, readonly string[]>

export type MessageChannel = keyof typeof SUBSTITUTED_BY_CHANNEL

/** What this channel's sender replaces. An unrecognised channel gets the narrowest list. */
export function substitutedVariables(channel: string): readonly string[] {
  return SUBSTITUTED_BY_CHANNEL[channel as MessageChannel] ?? SUBSTITUTED_BY_CHANNEL.push
}

/**
 * The three every channel substitutes.
 *
 * Kept as the name the rest of the module already used, so nothing that only needs *the common set* has to
 * know about channels - and narrowed deliberately: it is the intersection, not e-mail's list.
 */
export const SUBSTITUTED_VARIABLES = SUBSTITUTED_BY_CHANNEL.push

export type VariableName = (typeof SUBSTITUTED_BY_CHANNEL)['email'][number]

export interface VariableHelp {
  token: string
  description: string
}

/**
 * The legend, per channel — and it lists what the server substitutes, not what somebody hoped it
 * would (FB-094). Legacy's e-mail legend offers `{{email}}` and `{{programName}}`, neither of which
 * `render` has ever touched.
 */
export function variableHelp(channel = 'push'): VariableHelp[] {
  /* §9.484 - what **this** channel substitutes. One list for all three under-promised on e-mail, where
     `{{email}}` and `{{programName}}` work, and listing them everywhere would over-promise on push. */
  const described: Record<string, string> = {
    name: tt('Meno zákazníka, alebo „zákazník“ ak ho nemáme'),
    points: tt('Aktuálny počet bodov'),
    orders: tt('Počet objednávok'),
    email: tt('E-mailová adresa zákazníka'),
    programName: tt('Názov vernostného programu'),
  }
  return substitutedVariables(channel).map((name) => ({
    token: `{{${name}}}`,
    description: described[name] ?? '',
  }))
}

const VARIABLE_PATTERN = /\{\{?\s*(\w+)\s*\}?\}/g

/**
 * The `{{…}}` tokens in a message that the server will not replace.
 *
 * A message goes to thousands of people at once; a variable name nobody implemented arrives as
 * literal braces in every one of them. Named before the send rather than discovered afterwards.
 */
export function unknownVariables(text: string, channel = 'push'): string[] {
  const substituted = substitutedVariables(channel)
  const unknown = new Set<string>()
  for (const match of text.matchAll(VARIABLE_PATTERN)) {
    const name = match[1] ?? ''
    if (!substituted.includes(name)) unknown.add(name)
  }
  return [...unknown]
}

/**
 * The message as one customer will receive it.
 *
 * The same substitution the server does, so an operator sees the result before two thousand people
 * do. `{{name}}` falls back to `zákazník`, exactly as `render` does.
 */
export function renderForCustomer(template: string, customer: Customer): string {
  // Not a translatable string: it is the literal the *server* substitutes, so translating it here
  // would make the preview disagree with what is sent.
  // eslint-disable-next-line no-restricted-syntax -- mirrors CustomerBulkService.render's fallback
  const serverFallbackName = 'zákazník'
  const name = (customer.name ?? '').trim() === '' ? serverFallbackName : (customer.name as string)
  const points = String(customer.pointsBalance ?? 0)
  const orders = String(customer.orderCount ?? 0)
  return template
    .replace(/\{\{name\}\}/g, name)
    .replace(/\{name\}/g, name)
    .replace(/\{\{points\}\}/g, points)
    .replace(/\{points\}/g, points)
    .replace(/\{\{orders\}\}/g, orders)
    .replace(/\{orders\}/g, orders)
}

// ─── SMS: what is sent, and what it costs ────────────────────────────────────

/** Letters `Normalizer` cannot decompose, from `SmsGateway.STUBBORN_LETTERS`. */
const STUBBORN_LETTERS: Record<string, string> = {
  ľ: 'l',
  Ľ: 'L',
  ĺ: 'l',
  Ĺ: 'L',
  ł: 'l',
  Ł: 'L',
  đ: 'd',
  Đ: 'D',
  ŧ: 't',
  Ŧ: 'T',
  ŕ: 'r',
  Ŕ: 'R',
  ß: 'ss',
}

/**
 * Slovak without accents — the same transformation `SmsGateway.stripDiacritics` applies.
 *
 * **FB-095.** The gateway does this to every message before it goes out, and its javadoc says why:
 * *"the difference between one SMS segment and three"*. So the text an operator types is not the text
 * a customer reads, and the composer has to show which is which — otherwise the first anybody hears
 * of it is a customer asking what "Vazeny zakaznik" means.
 */
export function stripDiacritics(text: string): string {
  const decomposed = text.normalize('NFD').replace(/\p{M}+/gu, '')
  return [...decomposed]
    .map((letter) => (letter.codePointAt(0) ?? 0) < 128 ? letter : (STUBBORN_LETTERS[letter] ?? letter))
    .join('')
}

/** Whether the transliteration would change the message. */
export function willBeTransliterated(text: string): boolean {
  return stripDiacritics(text) !== text
}

/** A GSM-7 segment. The message is ASCII by the time it reaches the gateway. */
const SEGMENT_LENGTH = 160

/** Concatenated segments carry a header, which costs seven characters of each. */
const CONCATENATED_SEGMENT_LENGTH = 153

export interface SmsCost {
  /** The text the customer will actually receive. */
  sent: string
  characters: number
  segments: number
  recipients: number
  /** Segments × recipients — the number the gateway bills. */
  messages: number
  transliterated: boolean
}

/**
 * What the send will cost, counted on the text that will actually be sent.
 *
 * Legacy divides the typed length by 160 and stops there. Two corrections: the length is measured
 * after transliteration, because that is what leaves the gateway; and a message over one segment is
 * charged at 153 characters each, because the concatenation header takes seven. Then it is multiplied
 * by the recipients, which is the figure that appears on the bill and which legacy never shows.
 */
export function smsCost(message: string, recipients: number): SmsCost {
  const sent = stripDiacritics(message)
  const characters = sent.length
  const segments =
    characters <= SEGMENT_LENGTH
      ? Math.max(1, Math.ceil(characters / SEGMENT_LENGTH))
      : Math.ceil(characters / CONCATENATED_SEGMENT_LENGTH)
  return {
    sent,
    characters,
    segments,
    recipients,
    messages: segments * recipients,
    transliterated: sent !== message,
  }
}

// ─── the list ────────────────────────────────────────────────────────────────

export interface Filters {
  search: string
  loyalty: 'any' | 'yes' | 'no'
  orders: 'any' | 'yes' | 'no'
  emailOptIn: 'any' | 'in' | 'out'
  smsOptIn: 'any' | 'in' | 'out'
  /**
   * Push reachability — §9.283, the owner's third channel.
   *
   * Not spelled `in`/`out` like the two above it, because there is no opt-in column to be in or out
   * of: a push subscription **is** the consent, granted in the customer's own browser. `has` means a
   * registered device, `none` means never asked as much as it means refused.
   */
  push: 'any' | 'has' | 'none'
  deactivated: 'any' | 'yes' | 'no'
  level: string
  registeredFrom: string
  registeredTo: string
  referral: 'any' | 'referrer' | 'referred' | 'none'
  newsletter: 'any' | 'subscribed' | 'not_subscribed'
  includeNewsletterOnly: boolean
  /** An RFM segment key — §9.207. `any` is every customer, which is the default. */
  segment: string
}

/**
 * The segments Analytika names, in its funnel order — `analytics-report.SEGMENT_ORDER`.
 *
 * Duplicated as a list of labels rather than imported: `manager-customers` does not depend on
 * `manager-reporting` and should not start doing so for seven strings. The *definition* is not
 * duplicated — that lives in one SQL view (`BACKEND.md §10A.84`), which is the copy that would
 * actually matter if it drifted.
 */
export const SEGMENT_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'any', label: tt('Všetky segmenty') },
  { value: 'champions', label: tt('Šampióni') },
  { value: 'loyal', label: tt('Verní') },
  { value: 'newCustomers', label: tt('Noví') },
  { value: 'atRisk', label: tt('Riziko odchodu') },
  { value: 'hibernating', label: tt('Spiaci') },
  { value: 'occasional', label: tt('Príležitostní') },
  { value: 'lost', label: tt('Bez objednávky') },
]

/** Legacy's default hides deactivated accounts, which is the right way round. */
export const DEFAULT_FILTERS: Filters = {
  search: '',
  loyalty: 'any',
  orders: 'any',
  emailOptIn: 'any',
  smsOptIn: 'any',
  push: 'any',
  deactivated: 'no',
  level: 'any',
  registeredFrom: '',
  registeredTo: '',
  referral: 'any',
  newsletter: 'any',
  includeNewsletterOnly: false,
  segment: 'any',
}

export function activeFilterCount(filters: Filters): number {
  let count = 0
  if (filters.loyalty !== 'any') count += 1
  if (filters.orders !== 'any') count += 1
  if (filters.emailOptIn !== 'any') count += 1
  if (filters.smsOptIn !== 'any') count += 1
  if (filters.push !== 'any') count += 1
  if (filters.deactivated !== 'any') count += 1
  if (filters.level !== 'any') count += 1
  if (filters.registeredFrom !== '') count += 1
  if (filters.registeredTo !== '') count += 1
  if (filters.referral !== 'any') count += 1
  if (filters.newsletter !== 'any') count += 1
  if (filters.includeNewsletterOnly) count += 1
  if (filters.segment !== 'any') count += 1
  return count
}

/** The server's own cap, and legacy's request size. 2 464 customers fit inside it. */
export const SERVER_LIMIT = 10_000

/**
 * The query the list is loaded with. Every filter is the server's to apply.
 *
 * `loyalty` and the opt-in filters are *strings* on the controller, because "unset" is a third state
 * — `String loyalty` distinguishes absent from `"false"`, which a `boolean` parameter defaulting to
 * false cannot. `hasOrder`, `noOrder` and `includeNewsletterOnly` are real booleans, so they are sent
 * as booleans.
 */
export function listQuery(filters: Filters): ListQuery {
  const query: ListQuery = {
    limit: SERVER_LIMIT,
    offset: 0,
    sortBy: 'createdAt',
    sortDir: 'desc',
  }
  if (filters.search.trim() !== '') query.search = filters.search.trim()
  if (filters.loyalty === 'yes') query.loyalty = 'true'
  else if (filters.loyalty === 'no') query.loyalty = 'false'
  if (filters.orders === 'yes') query.hasOrder = true
  else if (filters.orders === 'no') query.noOrder = true
  if (filters.emailOptIn !== 'any') query.emailOptIn = filters.emailOptIn
  if (filters.smsOptIn !== 'any') query.smsOptIn = filters.smsOptIn
  // §10A.94 — the server's clause, not a pass over the loaded rows. The counts above the table are
  // its `total`; filtering here would make the header describe a different population from the list.
  if (filters.push !== 'any') query.push = filters.push
  if (filters.deactivated !== 'any') query.deactivated = filters.deactivated
  if (filters.level !== 'any') query.level = filters.level
  if (filters.registeredFrom !== '') query.registeredFrom = filters.registeredFrom
  if (filters.registeredTo !== '') query.registeredTo = filters.registeredTo
  if (filters.referral !== 'any') query.referral = filters.referral
  if (filters.newsletter !== 'any') query.newsletter = filters.newsletter
  if (filters.includeNewsletterOnly) query.includeNewsletterOnly = true
  // The server joins the segment view uncapped. The analytics list stops at 200 because it is a
  // screen; an audience cut at 200 is a campaign that missed everybody after it.
  if (filters.segment !== 'any') query.segment = filters.segment
  return query
}

export interface ListQuery {
  limit: number
  offset: number
  sortBy: string
  sortDir: string
  search?: string
  loyalty?: string
  hasOrder?: boolean
  noOrder?: boolean
  emailOptIn?: string
  smsOptIn?: string
  push?: string
  deactivated?: string
  level?: string
  registeredFrom?: string
  registeredTo?: string
  referral?: string
  newsletter?: string
  includeNewsletterOnly?: boolean
  segment?: string
}

/** Postgres prints `2026-05-05 10:00:00+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

export function levelKeyOf(customer: Customer): string {
  return (customer.customLevelKey ?? '') || (customer.loyaltyLevel ?? '') || ''
}

/**
 * Where a tier sits relative to the others.
 *
 * Legacy sorts by `-level.id`, with the comment *"higher id ≈ higher tier in many setups; tweak if
 * needed"* — a guess, and it is wrong the moment a franchise adds a tier in the middle. The order
 * comes from the level's own `minOrders` when it has one, and the known legacy enum otherwise.
 */
const LEGACY_TIER_ORDER: Record<string, number> = {
  elite_master: 1,
  gold_master: 2,
  gold: 3,
  silver: 4,
  bronze: 5,
}

export function tierRank(customer: Customer, levels: LoyaltyLevel[]): number {
  const key = levelKeyOf(customer)
  if (key === '') return 99
  const known = LEGACY_TIER_ORDER[key]
  if (known !== undefined) return known
  const index = levels.findIndex((level) => level.key === key)
  // An unknown custom tier sorts after every known one, in the order the franchise defined it.
  return index === -1 ? 99 : 50 + index
}

export interface DirectoryTotals {
  loaded: number
  total: number
  loyaltyTotal: number
  truncated: boolean
  newsletterOnly: number
  emailReachable: number
  smsReachable: number
  optedOutOfEmail: number
}

/**
 * The counts above the table.
 *
 * `optedOutOfEmail` is the one that matters: it is how many of the loaded customers have an address
 * and have said no, which is the population FB-093 would have mailed.
 */
export function directoryTotals(
  customers: Customer[],
  total: number,
  loyaltyTotal: number,
): DirectoryTotals {
  let newsletterOnly = 0
  let emailReachable = 0
  let smsReachable = 0
  let optedOutOfEmail = 0

  for (const customer of customers) {
    if (isNewsletterOnly(customer)) {
      newsletterOnly += 1
      continue
    }
    if (isEligible(customer, 'email')) emailReachable += 1
    if (isEligible(customer, 'sms')) smsReachable += 1
    if ((customer.email ?? '').trim() !== '' && customer.marketingEmailOptIn === false) {
      optedOutOfEmail += 1
    }
  }

  return {
    loaded: customers.length,
    total,
    loyaltyTotal,
    truncated: customers.length < total,
    newsletterOnly,
    emailReachable,
    smsReachable,
    optedOutOfEmail,
  }
}

export function matchesSearch(customer: Customer, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [customer.name, customer.email, customer.phone].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

// `selectableIds` was here and is deleted — §9.35, 2026-08-06. It answered *every row that can be
// selected at all*, excluding the newsletter-only ones, and the screen never asked.
//
// **Because every bulk action on it is channel-bound.** The two selections it offers are
// `reachableIds(customers, 'email')` and `reachableIds(customers, 'sms')`, and the buttons beneath
// them send exactly those. A plain select-all would gather people who opted out of e-mail and then
// hand them to *send e-mail* — the mistake `reachableIds` exists to prevent, one control away from
// being reintroduced.

/** Selecting for one channel selects only who can receive on it. */
export function reachableIds(customers: Customer[], channel: Channel): number[] {
  return customers
    .filter((customer) => customer.id !== undefined && isEligible(customer, channel))
    .map((customer) => customer.id as number)
}

// ─── points ──────────────────────────────────────────────────────────────────

export type PointsProblem = 'not-a-number' | 'zero' | 'not-whole' | 'nobody'

export function pointsProblems(raw: string, recipients: number): PointsProblem[] {
  const problems: PointsProblem[] = []
  const value = Number(raw.trim().replace(',', '.'))
  if (raw.trim() === '' || !Number.isFinite(value)) problems.push('not-a-number')
  else if (value === 0) problems.push('zero')
  else if (!Number.isInteger(value)) problems.push('not-whole')
  if (recipients === 0) problems.push('nobody')
  return problems
}

/**
 * What the toast says about telling the customers - FRONTEND.md §9.774, BACKEND.md §10A.428, owner O135.
 *
 * `notifying` is the server's count of customers it is telling, in the background, as *Pripísané body* is set in
 * *Notifikácie*. **Nought when somebody asked is a fact worth saying**: it means no channel switched on for the event
 * could reach them, which is exactly the *"I did not receive notification"* this entry is about - so it names where
 * to look rather than staying silent. Empty when nobody was asked to be told.
 */
export function pointsNoticeNote(asked: boolean, notifying: number | undefined): string {
  if (!asked) return ''
  const count = typeof notifying === 'number' && Number.isFinite(notifying) ? notifying : 0
  if (count > 0) {
    return `${tt('Upozornenie odchádza')} ${formatCount(count)} ${tt('zákazníkom podľa Nastavenia - Notifikácie, Pripísané body.')}`
  }
  return tt(
    'Upozornenie neodíde nikomu: body sa nezmenili, alebo pri udalosti Pripísané body v Nastavenia - Notifikácie nie je zapnutý kanál, ktorým by sa dal zákazník zastihnúť.',
  )
}

export function pointsDelta(raw: string): number {
  const value = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(value) ? Math.trunc(value) : 0
}

/**
 * What granting points does besides granting points.
 *
 * `adjustPoints` sets `is_loyalty = true` on a positive delta — because receiving points is what
 * being a member means — and clamps at zero on a negative one. Both are worth saying before the
 * button is pressed: granting 50 points to a hundred people enrols however many of them were not
 * members.
 */
export function enrolCount(customers: Customer[], selected: ReadonlySet<number>, delta: number): number {
  if (delta <= 0) return 0
  let count = 0
  for (const customer of customers) {
    if (customer.id === undefined || !selected.has(customer.id)) continue
    if (customer.isLoyalty !== true) count += 1
  }
  return count
}

/** How many would hit the floor, so "−100 each" does not read as 100 off everybody. */
export function clampedCount(
  customers: Customer[],
  selected: ReadonlySet<number>,
  delta: number,
): number {
  if (delta >= 0) return 0
  let count = 0
  for (const customer of customers) {
    if (customer.id === undefined || !selected.has(customer.id)) continue
    if ((customer.pointsBalance ?? 0) < -delta) count += 1
  }
  return count
}

// ─── the personal discount ───────────────────────────────────────────────────

export type DiscountProblem =
  | 'no-value'
  | 'percent-over-100'
  | 'negative'
  | 'more-than-they-spend'

/**
 * A permanent discount applied to every future order, which is why it is checked.
 *
 * Legacy accepts anything the number input allows: `max` is 100 for a percentage and 100 000 for a
 * fixed amount, and neither is enforced anywhere — `max` on an `<input type="number">` does not stop
 * a typed value. A fixed discount larger than the customer's average order is not invalid, but it is
 * worth a second look before it applies to every order they ever place again.
 */
export function discountProblems(
  type: 'percent' | 'fixed',
  raw: string,
  averageOrderCents: number,
): DiscountProblem[] {
  const problems: DiscountProblem[] = []
  const value = Number(raw.trim().replace(',', '.'))
  if (raw.trim() === '' || !Number.isFinite(value) || value === 0) {
    problems.push('no-value')
    return problems
  }
  if (value < 0) problems.push('negative')
  if (type === 'percent' && value > 100) problems.push('percent-over-100')
  if (type === 'fixed' && averageOrderCents > 0 && Math.round(value * 100) > averageOrderCents) {
    problems.push('more-than-they-spend')
  }
  return problems
}

export function averageOrderCents(customer: Customer): number {
  const orders = customer.orderCount ?? 0
  if (orders <= 0) return 0
  return Math.round(parseMoneyToCents(customer.totalSpent) / orders)
}

export function discountLabel(type: 'percent' | 'fixed' | null, value: MoneyInput): string {
  if (type === null) return ''
  if (type === 'percent') return `${Number(value ?? 0)} %`
  return formatCents(parseMoneyToCents(value))
}

export function hasPersonalDiscount(customer: Customer): boolean {
  return (
    (customer.personalDiscountType ?? null) !== null &&
    parseMoneyToCents(customer.personalDiscountValue) > 0
  )
}

// ─── anonymisation ───────────────────────────────────────────────────────────

/**
 * The phrase somebody has to type. Irreversible, so it names the count.
 *
 * Legacy asks for `ANONYMIZOVAT {count}` and compares with `trim()` only, so a lowercase answer is
 * refused without saying so. Compared case-insensitively here, with the whitespace collapsed — the
 * point of the phrase is deliberation, not typing accuracy.
 */
export function anonymisePhrase(count: number): string {
  return `ANONYMIZOVAT ${count}`
}

export function anonymiseConfirmed(typed: string, count: number): boolean {
  const normalise = (text: string) => text.trim().replace(/\s+/g, ' ').toUpperCase()
  return normalise(typed) === normalise(anonymisePhrase(count))
}

export interface AnonymiseProgress {
  status?: string
  processed?: number
  total?: number
  anonymized?: number
  failed?: number
  errorMessage?: string | null
}

export function progressPercent(progress: AnonymiseProgress | null): number {
  const total = progress?.total ?? 0
  if (total <= 0) return 0
  return Math.min(100, Math.round(((progress?.processed ?? 0) / total) * 100))
}

export function isFinished(progress: AnonymiseProgress | null): boolean {
  return progress?.status === 'done' || progress?.status === 'error'
}

export { formatCents, formatCount }

/**
 * What a bulk send actually reported - §9.364, FB-651.
 *
 * **The owner reported *"Odoslané: 0"* twice.** The screen read `sent` and `failed` out of an answer
 * carrying five or six fields, so every reason the server gave was thrown away:
 *
 *     error            why nothing was sent at all
 *     errors[]         one line per failed recipient - "empty rather than null when everything worked"
 *     skipped          asked-for less eligible. `CustomerDtos.BulkSend`'s own javadoc: *"A screen
 *                      that reports `sent` against `totalTargets` makes a consent filter look like a
 *                      delivery failure"*
 *     eligibleTargets  how many could lawfully be contacted
 *
 * **And `sent ?? recipients` was worse than the omission**: an answer with no `sent` field reported
 * every recipient as sent. A screen that invents a success out of a missing field is the one failure
 * mode a send screen must not have, so an absent count is now said to be unknown.
 *
 * `recipients` is passed for the *skipped* arithmetic only - never as a stand-in for `sent`.
 */
export interface BulkSendOutcome {
  title: string
  description?: string
  /** True only when something was actually sent, so the toast's colour follows the send. */
  ok: boolean
}

const MAX_LISTED_ERRORS = 3

export function bulkSendOutcome(result: unknown, recipients: number): BulkSendOutcome {
  const record = (result ?? {}) as Record<string, unknown>
  const number = (key: string): number | null =>
    typeof record[key] === 'number' ? record[key] : null
  const sent = number('sent')
  const failed = number('failed') ?? 0
  const eligible = number('eligibleTargets')
  const skipped = number('skipped') ?? (eligible === null ? null : recipients - eligible)
  const reason = typeof record.error === 'string' && record.error.trim() !== '' ? record.error : null
  const lines = Array.isArray(record.errors)
    ? record.errors.filter((line): line is string => typeof line === 'string' && line.trim() !== '')
    : []

  // **Nothing was sent and the server said why.** The only branch that used to show a bare zero.
  if (reason !== null && (sent === null || sent === 0)) {
    return { ok: false, title: tt('Neodoslané'), description: reason }
  }

  if (sent === null) {
    // An answer this screen cannot read. Reporting `recipients` here is what it used to do.
    return {
      ok: false,
      title: tt('Neznámy výsledok'),
      description: tt('Server neuviedol, koľkým sa odoslalo. Skontrolujte to pred opakovaním.'),
    }
  }

  const parts: string[] = []
  if (skipped !== null && skipped > 0) {
    // Named as what it is. A consent filter is not a delivery failure - the server's own warning.
    parts.push(`${skipped} ${tt('vynechaných (bez súhlasu alebo bez kontaktu)')}`)
  }
  if (failed > 0) {
    parts.push(
      lines.length === 0
        ? `${failed} ${tt('zlyhalo')}`
        : `${failed} ${tt('zlyhalo')}: ${lines.slice(0, MAX_LISTED_ERRORS).join('; ')}${
            lines.length > MAX_LISTED_ERRORS ? ' …' : ''
          }`,
    )
  }
  if (sent === 0 && parts.length === 0) {
    // `ok: true, sent: 0` with nothing else - §10A.158's shape before the backend learned to say so.
    // Kept because an older server, or a path nobody has measured, can still answer it.
    return {
      ok: false,
      title: `${tt('Odoslané:')} 0`,
      description: tt('Server neposlal nič a neuviedol dôvod.'),
    }
  }

  return {
    ok: sent > 0,
    title: `${tt('Odoslané:')} ${sent}`,
    description: parts.length === 0 ? undefined : parts.join(' · '),
  }
}

